import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";
import {
  X,
  Loader2,
  Download,
  Share2,
  Trash2,
  Link2,
  Copy,
  Check,
  Clock,
} from "lucide-react";
import ReportFullView from "./ReportFullView";
import ShareReportDialog from "./ShareReportDialog";

function fmtRelative(iso) {
  try {
    const ms = new Date(iso) - Date.now();
    const future = ms > 0;
    const abs = Math.abs(ms);
    const mins = Math.floor(abs / 60000);
    const hrs = Math.floor(mins / 60);
    const days = Math.floor(hrs / 24);
    if (days >= 1) return `${future ? "in " : ""}${days}d${future ? "" : " ago"}`;
    if (hrs >= 1) return `${future ? "in " : ""}${hrs}h${future ? "" : " ago"}`;
    return `${future ? "in " : ""}${mins}m${future ? "" : " ago"}`;
  } catch {
    return iso || "";
  }
}

/**
 * Admin-only modal that fetches the FULL report bundle, lets admin review every
 * captured data point, download PDF, and share via link to doctor/family.
 *
 * @param {{ reportId: string, onClose: () => void }} props
 */
export default function AdminReportDetail({ reportId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [shares, setShares] = useState([]);
  const [sharesOpen, setSharesOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get(`/reports/${reportId}/full`)
      .then(({ data }) => setData(data))
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Could not load report");
        onClose();
      })
      .finally(() => setLoading(false));
  };

  const loadShares = () => {
    api
      .get(`/reports/${reportId}/shares`)
      .then(({ data }) => setShares(data || []))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    loadShares();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API_BASE}/reports/${reportId}/pdf`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `afyanex-report-${reportId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const getAttachmentUrl = (attId) => {
    // Same-origin cookie auth: the browser attaches hc_auth automatically.
    return `${API_BASE}/attachments/${attId}/download`;
  };

  const onShareCreated = () => {
    setShowShare(false);
    loadShares();
  };

  const revoke = async (shareId) => {
    try {
      await api.post(`/reports/shares/${shareId}/revoke`);
      toast.success("Share link revoked");
      loadShares();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    }
  };

  const copyLink = async (token) => {
    const url = `${window.location.origin}/share/report/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-stretch sm:items-center justify-center bg-black/55 p-0 sm:p-4"
      onClick={onClose}
      data-testid="report-detail-modal"
    >
      <div
        className="bg-clinical-bg sm:rounded-2xl border-t sm:border border-clinical-border w-full sm:max-w-4xl h-full sm:max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-white flex items-center justify-between px-5 py-4 border-b border-clinical-border">
          <div className="min-w-0">
            <div className="label-eyebrow">Review report</div>
            <h2 className="font-heading text-lg font-semibold text-clinical-text truncate">
              {data?.client?.name || "Loading…"}
              {data?.report?.shift_date && (
                <span className="ml-2 text-sm font-normal text-clinical-subtext">
                  · {data.report.shift_date}
                </span>
              )}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowShare(true)}
              disabled={loading}
              className="btn-secondary"
              data-testid="open-share-button"
            >
              <Share2 className="h-4 w-4" /> Share
            </button>
            <button
              type="button"
              onClick={download}
              disabled={loading || downloading}
              className="btn-primary"
              data-testid="download-pdf-button"
            >
              {downloading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Preparing…
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" /> Download PDF
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost !p-1.5"
              data-testid="report-detail-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Existing shares accordion */}
        {shares.length > 0 && (
          <div className="bg-white border-b border-clinical-border" data-testid="existing-shares">
            <button
              type="button"
              onClick={() => setSharesOpen((v) => !v)}
              className="w-full flex items-center gap-2 px-5 py-2 text-xs text-clinical-subtext hover:bg-clinical-bg"
              data-testid="toggle-existing-shares"
            >
              <Link2 className="h-3.5 w-3.5" />
              {shares.filter((s) => !s.revoked).length} active share
              {shares.filter((s) => !s.revoked).length === 1 ? "" : "s"} · {shares.length} total
              <span className="ml-auto">{sharesOpen ? "Hide" : "Show"}</span>
            </button>
            {sharesOpen && (
              <ul className="divide-y divide-clinical-border px-5 pb-3">
                {shares.map((s) => {
                  const expired = new Date(s.expires_at) < new Date();
                  const inactive = s.revoked || expired;
                  return (
                    <li
                      key={s.id}
                      className={`py-2 flex items-center gap-3 text-xs ${inactive ? "opacity-60" : ""}`}
                      data-testid={`share-row-${s.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-clinical-text font-medium truncate">
                          {s.recipient_name || s.recipient_kind}
                          <span className="ml-2 text-clinical-subtext font-normal capitalize">
                            ({s.recipient_kind})
                          </span>
                        </div>
                        <div className="text-clinical-subtext flex items-center gap-2 mt-0.5">
                          <Clock className="h-3 w-3" />
                          {s.revoked
                            ? "Revoked"
                            : expired
                            ? "Expired"
                            : `Expires ${fmtRelative(s.expires_at)}`}
                          {s.access_count > 0 && (
                            <span>· viewed {s.access_count}×</span>
                          )}
                        </div>
                      </div>
                      {!inactive && (
                        <>
                          <button
                            type="button"
                            onClick={() => copyLink(s.token)}
                            className="btn-ghost !p-1.5"
                            data-testid={`copy-share-${s.id}`}
                            title="Copy link"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => revoke(s.id)}
                            className="btn-ghost !p-1.5 hover:!text-destructive"
                            data-testid={`revoke-share-${s.id}`}
                            title="Revoke"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-clinical-subtext">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading full report…
            </div>
          ) : data ? (
            <ReportFullView data={data} getAttachmentUrl={getAttachmentUrl} />
          ) : null}
        </div>

        {/* Footer note */}
        <div className="bg-white border-t border-clinical-border px-5 py-3 text-[11px] text-clinical-subtext">
          Confidential — PHI. Verify content before sharing externally.
        </div>
      </div>

      {showShare && data && (
        <ShareReportDialog
          reportId={reportId}
          clientName={data.client?.name || ""}
          onClose={onShareCreated}
        />
      )}
    </div>
  );
}
